import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Badge, BADGE_TYPES } from 'src/components/Elements/Badge';
import SubNav from 'src/components/Layout/SubNav';
import ProfileMenu from 'src/components/ProfileMenu';
import { CREDIT_LABEL } from 'src/constants/credit';
import { formatDollarAmount } from 'src/utils/formatter/currency';

const SCROLL_LIMIT = 200;

const AccountNavigation = ({ currentRoute, user }) => {
  const headerContainerRef = useRef();
  const headerRef = useRef();
  const badgeRef = useRef();
  const headerInitialFontSize = useRef(null);
  const headerInitialMarginTop = useRef(null);
  const badgeHeight = useRef(null); // need to control height as well since white space remains when scaling down
  const windowInnerHeight = useRef(null);
  const bodyScrollHeight = useRef(null);

  const setInitialValues = useCallback(() => {
    const { current: containerCurrent } = headerContainerRef;
    const { current } = headerRef;
    const fontSizeInPX = window.getComputedStyle(current).getPropertyValue('font-size');
    headerInitialFontSize.current = parseInt(fontSizeInPX, 10);
    const marginTopInPX = window.getComputedStyle(containerCurrent).getPropertyValue('margin-top');
    headerInitialMarginTop.current = parseInt(marginTopInPX, 10);
    const badgeHeightInPX = window.getComputedStyle(badgeRef.current).getPropertyValue('height');
    badgeHeight.current = parseInt(badgeHeightInPX, 10);

    windowInnerHeight.current = window.innerHeight;
    bodyScrollHeight.current = document.body.scrollHeight;
  }, []);

  const handleScroll = useCallback(() => {
    const { current: containerCurrent } = headerContainerRef;
    const { current } = headerRef;
    const { current: badgeCurrent } = badgeRef;
    if (!current) return;
    if (!headerInitialFontSize.current) setInitialValues();

    // don't shrink header if page content barely exceeds the browser window
    if (bodyScrollHeight.current - windowInnerHeight.current < SCROLL_LIMIT) return;

    const { scrollY } = window;

    if (scrollY <= 0) {
      current.style.fontSize = '';
      containerCurrent.style.marginTop = '';
      badgeCurrent.style.transform = '';
      badgeCurrent.style.height = '';
    } else if (scrollY > 0 && scrollY <= SCROLL_LIMIT) {
      const ratio = (SCROLL_LIMIT - scrollY) / SCROLL_LIMIT;
      const newFontSize = headerInitialFontSize.current * ratio;
      current.style.fontSize = `${newFontSize}px`;
      const newMargin = headerInitialMarginTop.current * ratio;
      containerCurrent.style.marginTop = `${newMargin}px`;
      badgeCurrent.style.transform = `scale(${ratio})`;
      badgeCurrent.style['transform-origin'] = 'left';
      badgeCurrent.style.height = `${badgeHeight.current * ratio}px`;
    } else {
      current.style.fontSize = 0;
      containerCurrent.style.marginTop = 0;
      badgeCurrent.style.transform = 'scale(0)';
      badgeCurrent.style.height = 0;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Credits
  const userJS = (user && user.toJS()) || {};
  const balance = userJS?.balance || 0;

  return (
    <SubNav>
      <div ref={headerContainerRef} className="flex alignItemsCenter marginTop-small">
        <h1 ref={headerRef} className="h3">
          My Account
        </h1>
        {!!balance && (
          <Badge
            ref={badgeRef}
            variant="outlined"
            size="medium"
            type={BADGE_TYPES.COMPLETED}
            classes="marginLeft-small"
          >
            {CREDIT_LABEL}: {formatDollarAmount({ amount: balance, truncateEvenDollars: true })}
          </Badge>
        )}
      </div>
      <ProfileMenu currentRoute={currentRoute} user={user} />
    </SubNav>
  );
};

AccountNavigation.propTypes = {
  currentRoute: PropTypes.string.isRequired,
  user: PropTypes.object,
};

export default AccountNavigation;
